import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { catchError, Observable, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { decodeJWT } from 'aws-amplify/auth';
import { UAMAuthService } from '../services/uam-auth.service';
import { AuthStateService } from '../services/auth-state.service';

@Injectable()
export class HttpApiInterceptor implements HttpInterceptor {
  idToken: string;
  refreshToken: string;

  constructor(
    private authService: UAMAuthService,
    private authStateService: AuthStateService
  ) {
    this.idToken = authService.getIdToken();
    this.refreshToken = authService.getRefreshToken();
  }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const endPoint = environment.endpoint;
    const excludedUrls = [
      `${endPoint}auth/settings`,
      '/assets/template/colors.json',
      `https://osd-rubi-assets.s3.us-gov-west-1.amazonaws.com/`,
    ];
    const refreshUrl = [`${environment.refreshEndpoint}`];

    if (
      !this.authStateService.isAutoRefreshEnabled() ||
      excludedUrls.some((x) => req.url.includes(x)) ||
      refreshUrl.some((x) => req.url.includes(x))
    ) {
      return next.handle(req.clone());
    }

    this.idToken = this.authService.getIdToken();
    this.refreshToken = this.authService.getRefreshToken();

    if (!this.idToken || this.idToken.trim() === '') {
      return next.handle(req.clone());
    }

    let decodedToken: ReturnType<typeof decodeJWT>;
    try {
      decodedToken = decodeJWT(this.idToken);
    } catch (error) {
      console.error('HttpApiInterceptor: Error decoding token', error);
      return next.handle(req.clone());
    }

    const tokenExpTime = decodedToken.payload.exp;
    const currTime = Math.floor(Date.now() / 1000);

    if (typeof tokenExpTime !== 'number') {
      console.warn('HttpApiInterceptor: JWT missing exp claim');
      return next.handle(req.clone());
    }

    if (currTime > tokenExpTime) {
      if (!this.refreshToken || this.refreshToken.trim() === '') {
        console.warn(
          'HttpApiInterceptor: Token expired but no refresh token available'
        );
        return next.handle(req.clone());
      }

      this.authService.getAuthTokens();
      this.refreshToken = this.authService.getRefreshToken();

      const deviceKey = this.authService.getDeviceKey();
      if (!deviceKey || deviceKey.trim() === '') {
        console.warn(
          'HttpApiInterceptor: Token expired but no device key available'
        );
        return next.handle(req.clone());
      }

      return this.authService.refreshTokenFunction(this.refreshToken).pipe(
        catchError((error) => {
          if (error.error?.errorMessage === 'refresh expired') {
            this.authService.redirectToUamLogin();
            return throwError(() => error);
          }

          console.warn(
            'HttpApiInterceptor: Token refresh failed, proceeding with original request',
            error
          );
          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${this.idToken}`,
            },
          });
          return next.handle(authReq);
        }),
        tap({
          next: (response: unknown) => {
            const newToken =
              this.authService.extractIdTokenFromRefreshResponse(response);
            if (!newToken) {
              console.error(
                'HttpApiInterceptor: Invalid refresh token response structure',
                response
              );
              throw new Error(
                'Invalid refresh token response: IdToken not found'
              );
            }

            this.idToken = newToken;
            this.authService.setIdToken(newToken);
          },
          error: (error) => {
            console.error(
              'HttpApiInterceptor: Error refreshing token in tap',
              error
            );
          },
        }),
        switchMap(() => {
          if (!this.idToken) {
            console.error(
              'HttpApiInterceptor: No token available after refresh'
            );
            this.authService.redirectToUamLogin();
            return throwError(
              () => new Error('No token available after refresh')
            );
          }

          const authReq = req.clone({
            setHeaders: {
              Authorization: `Bearer ${this.idToken}`,
            },
          });
          return next.handle(authReq);
        })
      );
    }

    const authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${this.idToken}`,
      },
    });
    return next.handle(authReq);
  }
}
